import { Injectable, NgZone } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  CreateBotConnection,
  CreateFacebookConnection,
  CreateFacebookConnectionWizard,
  DeleteFacebookConnectionWizard,
  GetFacebookConnection,
  GetFacebookPages,
  GetProvidersList,
  RemoveBotConnection,
  ResetFacebookAuthStatus,
  UpdateBotConnection,
  UpdateFacebookConnectionWizard,
} from './connections.actions';
import { ConnectionsService } from '../../services/connections.service';
import {
  ConnectionsStateModel,
  FacebookConnection,
  FacebookPage,
} from './connections-state.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { GetBotConnections } from '../bot/bot.actions';

@State<ConnectionsStateModel>({
  name: 'connections',
})
@Injectable()
export class ConnectionsState {
  constructor(
    private connectionsService: ConnectionsService,
    private snackbar: MatSnackBar,
    private zone: NgZone,
    public dialog: MatDialog
  ) {}

  /* ------------------------ Main connections actions ------------------------ */
  @Action(CreateBotConnection)
  createBotConnection(
    { patchState, dispatch }: StateContext<ConnectionsStateModel>,
    { provider, providerData }: CreateBotConnection
  ): Observable<any> {
    patchState({ connectionPendingAPIStatus: true });
    return this.connectionsService
      .createBotConnection(provider, providerData)
      .pipe(
        tap((res) => {
          if (res.status) {
            patchState({ connectionPendingAPIStatus: false });
            this.dialog.closeAll();
            this.snackbarZone('Connection created successfully');
            dispatch(new GetBotConnections());
            dispatch(new GetProvidersList());
          }
        }),
        // Handle request error
        catchError((error: HttpErrorResponse) => {
          patchState({ connectionPendingAPIStatus: false });
          // this.dialog.closeAll();
          this.snackbarZone(error.error.error, true);
          return throwError(error);
        })
      );
  }

  @Action(UpdateBotConnection)
  updateBotConnection(
    { patchState, dispatch }: StateContext<ConnectionsStateModel>,
    { provider, providerData }: UpdateBotConnection
  ): Observable<any> {
    patchState({ connectionPendingAPIStatus: true });
    return this.connectionsService
      .updateBotConnection(provider, providerData)
      .pipe(
        tap((res) => {
          if (res.status) {
            patchState({ connectionPendingAPIStatus: false });
            this.dialog.closeAll();
            this.snackbarZone('Connection updated successfully');
            dispatch(new GetBotConnections());
            dispatch(new GetProvidersList());
          }
        }),
        // Handle request error
        catchError((error: HttpErrorResponse) => {
          patchState({ connectionPendingAPIStatus: false });
          // this.dialog.closeAll();
          this.snackbarZone(error.error.error, true);
          return throwError(error);
        })
      );
  }

  @Action(RemoveBotConnection)
  removeBotConnection(
    { dispatch }: StateContext<ConnectionsStateModel>,
    { provider }: RemoveBotConnection
  ): Observable<any> {
    return this.connectionsService.removeBotConnection(provider).pipe(
      tap((res) => {
        if (res.status) {
          this.dialog.closeAll();
          this.snackbarZone('Connection removed successfully');
          dispatch(new GetBotConnections());
          dispatch(new GetProvidersList());
        }
      })
    );
  }

  @Action(GetProvidersList)
  getProvidersList({
    patchState,
  }: StateContext<ConnectionsStateModel>): Observable<any> {
    return this.connectionsService.getProvidersList().pipe(
      tap((res) => {
        patchState({ providers: res.providers });
      })
    );
  }

  @Action(GetFacebookConnection)
  getFacebookConnection({
    patchState,
  }: StateContext<ConnectionsStateModel>): Observable<FacebookConnection> {
    return this.connectionsService.getFacebookConnection().pipe(
      tap((res) => {
        patchState({ facebookConnection: res });
      })
    );
  }

  @Action(CreateFacebookConnection, { cancelUncompleted: true })
  createFacebookConnection(
    { getState }: StateContext<ConnectionsStateModel>,
    { fdPage }: CreateFacebookConnection
  ): any {
    // if (fdPage) {
    //   const fbConnection = {
    //     provider_data: {
    //       user_token: fdPage.access_token,
    //       page_id: fdPage.id,
    //       page_name: fdPage.name,
    //       get_started: {
    //         status: false,
    //       },
    //     },
    //   };

    return this.connectionsService.createFacebookConnection(fdPage).pipe(
      tap(
        (res) => {
          if (res.status) {
            this.zone.run(() => {
              this.snackbar.open('Facebook connection created!', '', {
                duration: 3000,
                panelClass: 'success',
              });
            });
          }
        },
        (error) => {
          this.zone.run(() => {
            this.snackbar.open(error.error.error, '', {
              duration: 3000,
              panelClass: 'error',
            });
          });
        }
      )
    );
  }

  @Action(CreateFacebookConnectionWizard, { cancelUncompleted: true })
  createFacebookConnectionWizard(
    { getState }: StateContext<ConnectionsStateModel>,
    { fdPage, botId }: CreateFacebookConnectionWizard
  ): any {
    if (fdPage) {
      const fbConnection = {
        provider_data: {
          user_token: fdPage.access_token,
          page_id: fdPage.id,
          page_name: fdPage.name,
          get_started: {
            status: false,
          },
        },
      };

      return this.connectionsService
        .createFacebookConnection(fbConnection, botId)
        .pipe(
          tap(
            () => {},
            (error) => {
              this.zone.run(() => {
                this.snackbar.open(error.error.error, '', {
                  duration: 3000,
                  panelClass: 'error',
                });
              });
            }
          )
        );
    }
  }

  @Action(UpdateFacebookConnectionWizard, { cancelUncompleted: true })
  updateFacebookConnectionWizard(
    { getState }: StateContext<ConnectionsStateModel>,
    { fdPage, botId }: UpdateFacebookConnectionWizard
  ): any {
    if (fdPage) {
      const fbConnection = {
        provider_data: {
          user_token: fdPage.access_token,
          page_id: fdPage.id,
          page_name: fdPage.name,
          get_started: {
            status: false,
          },
        },
      };

      return this.connectionsService
        .updateFacebookConnection(fbConnection, botId)
        .pipe(
          tap(
            () => {},
            (error) => {
              this.zone.run(() => {
                this.snackbar.open(error.error.error, '', {
                  duration: 3000,
                  panelClass: 'error',
                });
              });
            }
          )
        );
    }
  }

  @Action(DeleteFacebookConnectionWizard)
  deleteFacebookConnectionWizard(
    { getState }: StateContext<ConnectionsStateModel>,
    { botId }: DeleteFacebookConnectionWizard
  ): any {
    return this.connectionsService.deleteFacebookConnection(botId).pipe(
      tap(
        () => {},
        (error) => {
          this.zone.run(() => {
            this.snackbar.open(error.error.error, '', {
              duration: 3000,
              panelClass: 'error',
            });
          });
        }
      )
    );
  }

  @Action(GetFacebookPages)
  getFacebookPages(
    { patchState }: StateContext<ConnectionsStateModel>,
    { instagramPermission }: GetFacebookPages
  ): Observable<FacebookPage[]> {
    patchState({ facebookAuthStatus: 'pending' });
    return this.connectionsService.getFacebookPages(instagramPermission).pipe(
      tap((res) => {
        if (res.length) {
          patchState({ facebookAuthStatus: 'connected' });
        }
        patchState({ facebookPages: res });
      })
    );
  }

  @Action(ResetFacebookAuthStatus)
  resetFacebookAuthStatus({
    patchState,
  }: StateContext<ConnectionsStateModel>): void {
    patchState({ facebookPages: [], facebookAuthStatus: 'not_connected' });
  }

  private snackbarZone(
    msg: string,
    isError?: boolean,
    duration?: number
  ): void {
    this.zone.run(() => {
      this.snackbar.open(msg, '', {
        duration: duration || 5000,
        panelClass: isError ? 'error' : 'success',
      });
    });
  }
}
